import {FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Button, Modal} from "react-bootstrap";
import {useKeyboardShortcut} from "Hooks/useKeyboardShortcut";
import { classUtils as c } from 'Vendor/Utils/ClassUtils'
import {Folder, PreviewEntry, PreviewTree} from "./BimboShareFolders";

type PreviewCanvasProps<P> = {
    show: boolean
    handleClose: () => void
    title: string
    tree: PreviewTree<P>
    startKey?: string
    renderImage: (entry: PreviewEntry<P>) => ReactNode
    renderDetails: (entry: PreviewEntry<P>) => ReactNode
}

function PreviewCanvas<P>({
    show,
    handleClose,
    title,
    tree,
    startKey,
    renderImage,
    renderDetails,
}: PreviewCanvasProps<P>) {
    const activeItemRef = useRef<HTMLButtonElement>(null)
    const [position, setPosition] = useState<number>(0)
    const [collapsed, setCollapsed] = useState<Set<string>>(new Set())

    const {root, entries} = tree
    const entry = entries[position] ?? entries[0]
    const isGrouped = useMemo(() => root.folders.length > 0, [root])

    useEffect(() => {
        if (show) {
            const start = startKey ? entries.findIndex(candidate => candidate.key === startKey) : -1
            setPosition(start >= 0 ? start : 0)
            setCollapsed(new Set())
        }
    }, [show, startKey, entries])

    // Stepping onto an entry inside a collapsed folder opens that folder, so the highlighted row stays visible.
    useEffect(() => {
        if (!entry) {
            return
        }

        const ancestors = entry.path.map((_, depth) => '/' + entry.path.slice(0, depth + 1).join('/'))
        setCollapsed(previous => {
            if (!ancestors.some(key => previous.has(key))) {
                return previous
            }

            const next = new Set(previous)
            ancestors.forEach(key => next.delete(key))

            return next
        })
    }, [entry])

    const toggleFolder = (key: string) => {
        setCollapsed(previous => {
            const next = new Set(previous)
            if (next.has(key)) {
                next.delete(key)
            } else {
                next.add(key)
            }

            return next
        })
    }

    const handlePrevious = useCallback(() => {
        setPosition((entry.position - 1 + entries.length) % entries.length)
    }, [entry, entries.length])

    const handleNext = useCallback(() => {
        setPosition((entry.position + 1) % entries.length)
    }, [entry, entries.length])

    useKeyboardShortcut({key: 'ArrowLeft', onKeyPressed: handlePrevious, isEnabled: show && entries.length > 1})
    useKeyboardShortcut({key: 'ArrowRight', onKeyPressed: handleNext, isEnabled: show && entries.length > 1})

    const scrollActiveItemIntoView = useCallback(() => {
        activeItemRef.current?.scrollIntoView({block: 'nearest'})
    }, [])

    useEffect(() => scrollActiveItemIntoView(), [position, collapsed, scrollActiveItemIntoView])

    const renderFolder = (folder: Folder<P>): ReactNode => <>
        {folder.folders.map(child => {
            const isOpen = !collapsed.has(child.key)

            return <li key={child.key}>
                <button type="button" className="folder" aria-expanded={isOpen} title={child.name}
                        onClick={() => toggleFolder(child.key)}
                >
                    <i className={c('fa-solid caret', isOpen ? 'fa-caret-down' : 'fa-caret-right')}></i>
                    <i className={c('fa-solid folder-icon', isOpen ? 'fa-folder-open' : 'fa-folder')}></i>
                    <span className="name">{child.name}</span>
                    <span className="count">{child.count}</span>
                </button>
                {isOpen && <ul>{renderFolder(child)}</ul>}
            </li>
        })}
        {folder.entries.map(folderEntry =>
            <li key={folderEntry.key}>
                <button type="button"
                        ref={folderEntry === entry ? activeItemRef : undefined}
                        className={c('entry', folderEntry === entry && 'active')}
                        title={folderEntry.name}
                        onClick={() => setPosition(folderEntry.position)}
                >
                    <span className="icon">
                        {folderEntry.thumbnailSrc
                            ? <img src={folderEntry.thumbnailSrc} alt={folderEntry.name}/>
                            : <i className="fa-regular fa-file"></i>
                        }
                    </span>
                    <span className="name">{folderEntry.name}</span>
                </button>
            </li>
        )}
    </>

    return (
        <Modal show={show} onHide={handleClose} onEntered={scrollActiveItemIntoView} fullscreen
               className="bimbo-share-preview-modal">
            <Modal.Header closeButton>
                <Modal.Title>
                    {title}
                    {entry && <span className="counter">{entry.position + 1} / {entries.length}</span>}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {entry && <>
                    <ul className={c('items', !isGrouped && 'flat')}>
                        {renderFolder(root)}
                    </ul>
                    <div className="preview">
                        <div className="visual">
                            {entries.length > 1 &&
                                <Button onClick={handlePrevious} className="previous">
                                    <i className="fa-solid fa-chevron-left"></i>
                                </Button>
                            }
                            {renderImage(entry)}
                            {entries.length > 1 &&
                                <Button onClick={handleNext} className="next">
                                    <i className="fa-solid fa-chevron-right"></i>
                                </Button>
                            }
                        </div>
                        <div className="details">
                            {entry.path.length > 0 &&
                                <div className="folder-path">
                                    <i className="fa-solid fa-folder-open"></i>
                                    {entry.path.join(' › ')}
                                </div>
                            }
                            {renderDetails(entry)}
                        </div>
                    </div>
                </>}
            </Modal.Body>
        </Modal>
    )
}

export default PreviewCanvas as <P>(props: PreviewCanvasProps<P>) => ReturnType<FC>
